import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import CustomSnackBar from "../../Components/CustomSnackBar";
import SalePaymentDialog from "../../Components/sale/SalePaymentDialog";
import usePostSaleInvoiceFlow from "../../Components/invoice/usePostSaleInvoiceFlow";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../config/permissionUtils";
import { onCashSessionChanged, emitCashSessionChanged } from "../../services/http/cashEvents";
import { cashService } from "../../services/modules/cash.service";
import { saleService } from "../../services/modules/sale.service";
import MainText from "../../Components/MainText";
import ActiveGeneralInventoryAlert from "../Inventory/components/ActiveGeneralInventoryAlert";
import { PointOfSale, RefreshOutlined } from "@mui/icons-material";

const STATUS_TABS = [
  { value: "all", label: "Pendientes", statuses: ["PENDING", "WAITING_PAYMENT"] },
  { value: "waiting_payment", label: "Esperando pago", statuses: ["WAITING_PAYMENT"] },
];

const STATUS_LABELS = {
  PENDING: "Pendiente",
  WAITING_PAYMENT: "Esperando pago",
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "Q0.00";
  return `Q${parsed.toFixed(2)}`;
};

const getTicketCashierName = (sale) =>
  sale?.sold_by_display ||
  sale?.created_by_name ||
  sale?.authorized_by_display ||
  "-";

function CashboxPage() {
  const { currentBranch, isSuperuser, effectivePerms = [] } = useAuth();
  const branchId = currentBranch?.id || null;
  const canPayNow = hasPermission(effectivePerms, "sale_pay_now", isSuperuser);
  const canAuthorizePayment = hasPermission(effectivePerms, "sale_authorize_payment", isSuperuser);
  const canViewTransferPayment = canAuthorizePayment;
  const canVoidSale = hasPermission(effectivePerms, "sale_void", isSuperuser);

  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cashOpen, setCashOpen] = useState(false);
  const [salesConfig, setSalesConfig] = useState({ allow_cashier_queue: true });

  const [selectedSale, setSelectedSale] = useState(null);
  const [paymentDetailSale, setPaymentDetailSale] = useState(null);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [paymentDetailOpen, setPaymentDetailOpen] = useState(false);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [voidSubmitting, setVoidSubmitting] = useState(false);
  const [paymentDetailLoading, setPaymentDetailLoading] = useState(false);
  const [transferAccounts, setTransferAccounts] = useState([]);
  const [loadingTransferAccounts, setLoadingTransferAccounts] = useState(false);
  const [voidDialog, setVoidDialog] = useState({
    open: false,
    sale: null,
    reason: "",
  });

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);
  const { handlePostSaleInvoiceFlow, invoiceDialog } = usePostSaleInvoiceFlow({ showSnack });

  const activeStatuses = useMemo(() => {
    const selectedTab = STATUS_TABS.find((entry) => entry.value === tab);
    return selectedTab?.statuses || STATUS_TABS[0].statuses;
  }, [tab]);

  const cashierQueueEnabled = salesConfig?.allow_cashier_queue !== false;

  const loadSalesConfig = useCallback(async () => {
    if (!branchId) {
      setSalesConfig({ allow_cashier_queue: true });
      return;
    }
    try {
      const data = await saleService.getSalesConfig({ branch_id: branchId });
      setSalesConfig({
        branch_id: data?.branch_id || branchId,
        allow_cashier_queue: data?.allow_cashier_queue !== false,
      });
    } catch {
      setSalesConfig({ branch_id: branchId, allow_cashier_queue: true });
    }
  }, [branchId]);

  const loadCurrentCash = useCallback(async () => {
    if (!branchId) {
      setCashOpen(false);
      return;
    }
    try {
      const data = await cashService.currentSession({ branch_id: branchId });
      setCashOpen(Boolean(data?.session && data.session.status === "OPEN"));
    } catch {
      setCashOpen(false);
    }
  }, [branchId]);

  const loadRows = useCallback(async () => {
    if (!branchId) {
      setRows([]);
      return;
    }
    if (!cashierQueueEnabled) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const data = await saleService.listSales({
        branch_id: branchId,
        statuses: activeStatuses.join(","),
        with_items: true,
      });
      const nextRows = toArray(data).filter((sale) => {
        const status = String(sale?.status || "").toUpperCase();
        const itemsCount = Number(sale?.items_count || 0);
        return itemsCount > 0 && (status === "PENDING" || status === "WAITING_PAYMENT");
      });
      setRows(nextRows);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [activeStatuses, branchId, cashierQueueEnabled]);

  const loadPendingCount = useCallback(async () => {
    if (!branchId) {
      setPendingCount(0);
      return;
    }
    if (!cashierQueueEnabled) {
      setPendingCount(0);
      return;
    }

    try {
      const data = await saleService.pendingCount({ branch_id: branchId });
      setPendingCount(Number(data?.count || 0));
    } catch {
      setPendingCount(0);
    }
  }, [branchId, cashierQueueEnabled]);

  const loadTransferAccounts = useCallback(async () => {
    if (!branchId) {
      setTransferAccounts([]);
      return;
    }
    setLoadingTransferAccounts(true);
    try {
      const data = await saleService.listTransferAccounts({ branch_id: branchId, active: true });
      setTransferAccounts(toArray(data));
    } catch {
      setTransferAccounts([]);
    } finally {
      setLoadingTransferAccounts(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadSalesConfig();
  }, [loadSalesConfig]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    loadPendingCount();
    loadCurrentCash();
    loadTransferAccounts();
  }, [loadCurrentCash, loadPendingCount, loadTransferAccounts]);

  useEffect(() => {
    const unsubscribe = onCashSessionChanged((payload) => {
      const payloadBranchId = payload?.branchId ?? null;
      if (!branchId || (payloadBranchId && payloadBranchId !== branchId)) return;
      loadRows();
      loadPendingCount();
      loadCurrentCash();
    });
    return unsubscribe;
  }, [branchId, loadCurrentCash, loadPendingCount, loadRows]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([loadSalesConfig(), loadRows(), loadPendingCount(), loadCurrentCash()]);
  }, [loadCurrentCash, loadPendingCount, loadRows, loadSalesConfig]);

  const handleOpenPayDialog = useCallback(async (row) => {
    if (!branchId || !row?.id) return;
    if (!canPayNow) {
      showSnack("No autorizado para cobrar al instante.", "warning");
      return;
    }

    try {
      const detail = await saleService.getSale(row.id, { branch_id: branchId });
      setSelectedSale(detail || null);
      setPayDialogOpen(true);
    } catch {
      showSnack("No se pudo cargar el detalle de la venta.", "error");
    }
  }, [branchId, canPayNow, showSnack]);

  const handleOpenPaymentDetail = useCallback(async (row) => {
    if (!branchId || !row?.id) return;
    setPaymentDetailLoading(true);
    try {
      const detail = await saleService.getSale(row.id, { branch_id: branchId });
      setPaymentDetailSale(detail || null);
      setPaymentDetailOpen(true);
    } catch {
      showSnack("No se pudo cargar el detalle del pago.", "error");
    } finally {
      setPaymentDetailLoading(false);
    }
  }, [branchId, showSnack]);

  const handleVoidSale = useCallback((row) => {
    if (!branchId || !row?.id) return;
    if (!canVoidSale) {
      showSnack("No autorizado para anular ventas.", "warning");
      return;
    }
    setVoidDialog({
      open: true,
      sale: row,
      reason: "",
    });
  }, [branchId, canVoidSale, showSnack]);

  const handleConfirmVoidSale = useCallback(async () => {
    const sale = voidDialog.sale;
    const reason = String(voidDialog.reason || "").trim();
    if (!branchId || !sale?.id) return;
    if (!reason) {
      showSnack("Debes ingresar el motivo de anulacion.", "warning");
      return;
    }
    setVoidSubmitting(true);
    try {
      await saleService.voidSale(sale.id, { branch_id: branchId, reason });
      showSnack("Venta anulada.", "success");
      setVoidDialog({ open: false, sale: null, reason: "" });
      emitCashSessionChanged({ branchId, action: "sale_voided", saleId: sale.id });
      await Promise.all([loadRows(), loadPendingCount()]);
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo anular la venta.";
      showSnack(detail, "error");
    } finally {
      setVoidSubmitting(false);
    }
  }, [branchId, loadPendingCount, loadRows, showSnack, voidDialog.reason, voidDialog.sale]);

  const handleAuthorizePayment = useCallback(async (row) => {
    if (!branchId || !row?.id) return;
    if (!canAuthorizePayment) {
      showSnack("No autorizado para aprobar pagos.", "warning");
      return;
    }
    if (!cashOpen) {
      showSnack("Debe abrir caja para aprobar el pago.", "warning");
      return;
    }

    try {
      const updatedSale = await saleService.authorizePayment(row.id, {
        branch_id: branchId,
        comment: "Aprobado desde Caja",
      });
      await handlePostSaleInvoiceFlow({
        sale: updatedSale,
        branchName: updatedSale?.branch_name || currentBranch?.name || "-",
        cashierName: getTicketCashierName(updatedSale),
        allowTicketPrint: true,
      });
      showSnack("Pago aprobado correctamente.", "success");
      emitCashSessionChanged({ branchId, action: "sale_authorized", saleId: row.id });
      await Promise.all([loadRows(), loadPendingCount()]);
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo aprobar el pago.";
      showSnack(detail, "error");
    }
  }, [branchId, canAuthorizePayment, cashOpen, currentBranch?.name, handlePostSaleInvoiceFlow, loadPendingCount, loadRows, showSnack]);

  const handleConfirmPay = async (paymentPayload) => {
    if (!branchId || !selectedSale?.id) return;
    if (!cashOpen) {
      showSnack("Debe abrir caja para cobrar.", "warning");
      return;
    }

    const method = String(paymentPayload?.payment_method || "CASH").toUpperCase();
    const total = Number(selectedSale?.total || 0);
    const referenceParts = [paymentPayload?.reference || ""];
    if (paymentPayload?.bank) {
      referenceParts.push(`Banco: ${paymentPayload.bank}`);
    }
    if (paymentPayload?.payment_date) {
      referenceParts.push(`Fecha: ${paymentPayload.payment_date}`);
    }
    const composedReference = referenceParts.filter(Boolean).join(" | ");
    const shouldAutoPrint = method === "CASH" || method === "CARD" || method === "VOUCHER";

    setPaySubmitting(true);
    try {
      const updated = await saleService.paySale(selectedSale.id, {
        branch_id: branchId,
        payments: [
          {
            payment_method: method,
            amount: total.toFixed(2),
            cash_received:
              method === "CASH" ? paymentPayload?.cash_received || total.toFixed(2) : undefined,
            reference: composedReference,
            account_id: method === "TRANSFER" ? paymentPayload?.account_id : undefined,
          },
        ],
      });
      setPayDialogOpen(false);
      setSelectedSale(null);
      const nextStatus = String(updated?.status || "").toUpperCase();
      if (nextStatus === "WAITING_PAYMENT") {
        showSnack("Transferencia registrada. Quedo esperando aprobacion.", "info");
      } else {
        await handlePostSaleInvoiceFlow({
          sale: updated,
          branchName: updated?.branch_name || currentBranch?.name || "-",
          cashierName: getTicketCashierName(updated),
          allowTicketPrint: shouldAutoPrint,
        });
        showSnack("Venta cobrada correctamente.", "success");
      }
      emitCashSessionChanged({ branchId, action: "sale_paid", saleId: selectedSale.id });
      await Promise.all([loadRows(), loadPendingCount()]);
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo cobrar la venta.";
      showSnack(detail, "error");
    } finally {
      setPaySubmitting(false);
    }
  };

  const transferPayments = useMemo(
    () =>
      toArray(paymentDetailSale?.payments).filter(
        (payment) => String(payment?.payment_method_code || "").toUpperCase() === "TRANSFER"
      ),
    [paymentDetailSale?.payments]
  );

  const columns = useMemo(
    () => [
      { field: "sale_number", headerName: "Ticket", width: 160 },
      {
        field: "primary_payment_label",
        headerName: "Pago",
        width: 150,
        valueGetter: (_, row) => row?.primary_payment_label || "-",
      },
      {
        field: "status",
        headerName: "Estado",
        width: 180,
        valueGetter: (_, row) => STATUS_LABELS[row?.status] || row?.status || "-",
      },
      {
        field: "items_count",
        headerName: "Items",
        width: 110,
        type: "number",
        align: "center",
        headerAlign: "center",
        valueGetter: (_, row) => Number(row?.items_count || 0),
      },
      { field: "total", headerName: "Total", width: 130, valueGetter: (_, row) => toMoney(row?.total) },
      {
        field: "created_at",
        headerName: "Creada",
        flex: 1,
        minWidth: 220,
        valueGetter: (_, row) => formatDateTime(row?.created_at),
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 340,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {row?.status === "WAITING_PAYMENT" ? (
              <>
                {canViewTransferPayment ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleOpenPaymentDetail(row)}
                    disabled={paymentDetailLoading}
                  >
                    Ver pago
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, alignSelf: "center" }}>
                    Sin permiso
                  </Typography>
                )}
                {canAuthorizePayment ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => handleAuthorizePayment(row)}
                    disabled={!cashOpen}
                  >
                    Aprobar pago
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, alignSelf: "center" }}>
                    Sin permiso
                  </Typography>
                )}
              </>
            ) : (
              canPayNow ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleOpenPayDialog(row)}
                  disabled={!cashOpen}
                >
                  Cobrar
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, alignSelf: "center" }}>
                  Sin permiso
                </Typography>
              )
            )}
            {canVoidSale ? (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleVoidSale(row)}
                disabled={voidSubmitting}
              >
                Anular
              </Button>
            ) : null}
          </Stack>
        ),
      },
    ],
    [
      canAuthorizePayment,
      canPayNow,
      canViewTransferPayment,
      canVoidSale,
      cashOpen,
      handleAuthorizePayment,
      handleOpenPayDialog,
      handleOpenPaymentDetail,
      handleVoidSale,
      paymentDetailLoading,
      voidSubmitting,
    ]
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <MainText icon={PointOfSale} title={"Caja"} subtitle={"Pedidos pendientes de confirmar"} />

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            size="small"
            color="default"
            label={`${pendingCount} pendiente${pendingCount === 1 ? "" : "s"}`}
          />
          <Tooltip title="Refrescar">
            <span>
              <IconButton size="small" onClick={handleRefresh} disabled={loading}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <ActiveGeneralInventoryAlert />

      <Paper variant="outlined" sx={{ p: 1 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto">
          {STATUS_TABS.map((statusTab) => (
            <Tab key={statusTab.value} value={statusTab.value} label={statusTab.label} />
          ))}
        </Tabs>
      </Paper>

      {!cashierQueueEnabled ? (
        <Alert severity="info">
          La bandeja de caja de cobro no esta habilitada para esta sucursal.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ height: "calc(100vh - 300px)", minHeight: 360 }}>
        <DataGrid
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#757B2B",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover":
              {
                backgroundColor: "#757B2B !important",
              },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
              {
                color: "#ffffff !important",
              },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(16, 23, 60, 0.08)",
            },
          }}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
        />
      </Paper>

      <SalePaymentDialog
        open={payDialogOpen}
        onCancel={() => setPayDialogOpen(false)}
        onConfirm={handleConfirmPay}
        total={Number(selectedSale?.total || 0)}
        itemsCount={toArray(selectedSale?.items).length}
        hasOpenCashSession={cashOpen}
        transferAccounts={transferAccounts}
        loadingTransferAccounts={loadingTransferAccounts}
        submitting={paySubmitting}
      />

      <Dialog
        open={paymentDetailOpen}
        onClose={() => setPaymentDetailOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Detalle de pago</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {paymentDetailSale?.sale_number || "Ticket"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Estado: {STATUS_LABELS[paymentDetailSale?.status] || paymentDetailSale?.status || "-"} · Total:{" "}
                {toMoney(paymentDetailSale?.total)}
              </Typography>
            </Box>
            <Divider />
            {transferPayments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Esta venta no tiene pagos de transferencia para revisar.
              </Typography>
            ) : (
              transferPayments.map((payment) => (
                <Paper key={payment.id} variant="outlined" sx={{ p: 1.25 }}>
                  <Stack spacing={0.8}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {payment.payment_method_name || "Transferencia"}
                      </Typography>
                      <Chip size="small" color="info" label={toMoney(payment.amount)} />
                    </Stack>
                    <Typography variant="body2">
                      Cuenta destino: {payment.company_bank_account_name || "-"}
                    </Typography>
                    <Typography variant="body2">
                      Banco: {payment.company_bank_name || "-"}
                    </Typography>
                    <Typography variant="body2">
                      No. cuenta: {payment.company_bank_account_number || "-"}
                    </Typography>
                    <Typography variant="body2">
                      Tipo: {payment.company_bank_account_type || "-"}
                    </Typography>
                    <Typography variant="body2">
                      Referencia: {payment.reference || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Registrado: {formatDateTime(payment.created_at)}
                    </Typography>
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={voidDialog.open}
        onClose={() => {
          if (voidSubmitting) return;
          setVoidDialog({ open: false, sale: null, reason: "" });
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Anular venta</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              Esta accion anulara la venta y, si ya tenia movimientos aplicados, tambien dejara sus movimientos de caja como anulados.
            </Typography>
            <Typography variant="body2">
              Ticket: {voidDialog.sale?.sale_number || "-"}
            </Typography>
            <TextField
              label="Motivo de anulacion"
              value={voidDialog.reason}
              onChange={(event) =>
                setVoidDialog((prev) => ({
                  ...prev,
                  reason: event.target.value,
                }))
              }
              fullWidth
              size="small"
              multiline
              minRows={3}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setVoidDialog({ open: false, sale: null, reason: "" })}
            disabled={voidSubmitting}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmVoidSale}
            disabled={voidSubmitting || !String(voidDialog.reason || "").trim()}
          >
            {voidSubmitting ? "Anulando..." : "Confirmar anulación"}
          </Button>
        </DialogActions>
      </Dialog>

      {invoiceDialog}
      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default CashboxPage;
